import classNames from "classnames";
import * as React from "react";
import ReactTooltip from "react-tooltip";

import style from "./button.scss";
import Export from "components/icons/Export";
import Print from "components/icons/Print";
import RefreshIcon from "components/icons/RefreshIcon";
import SimpleDeleteIcon from "components/icons/SimpleDeleteIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { useTheme } from "utils/useTheme";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant: Variant;
    icon?: Icon;
    customIcon?: JSX.Element;
    testId?: string;
    tooltipText?: string;
    disabled?: boolean;
    isSmallButton?: boolean;
    loadingAnimation?: boolean;
}

// Variants follow the BMP Design system in Figma. Don't add your own if it's not in the design system!
type Variant = "PRIMARY" | "SECONDARY" | "TERTIARY";
type Icon = "EXPORT" | "DELETE" | "PRINT" | "RECHECK";

const Button = React.forwardRef<HTMLButtonElement, Props>(function Button(props, ref) {
    const { variant, icon, customIcon, testId, tooltipText, isSmallButton, loadingAnimation, ...buttonProps } = props;
    const theme = useTheme();
    const buttonHasIcon = customIcon || icon || loadingAnimation;

    const buttonIcon = () => {
        if (!buttonHasIcon) {
            return;
        }

        if (customIcon) {
            return customIcon;
        }

        if (loadingAnimation) {
            return <LoadingIndicator smaller={true} isWhite={props.variant === "PRIMARY"} />;
        }

        switch (icon) {
            case "EXPORT":
                return <Export color={theme.iconFillColor} />;
            case "DELETE":
                return <SimpleDeleteIcon color={theme.errorBackgroundColor} />;
            case "PRINT":
                return <Print color={theme.iconFillColor} />;
            case "RECHECK":
                return <RefreshIcon color={theme.iconFillColor} />;
            default:
                return;
        }
    };

    const buttonClasses = classNames(style.button, style.buttonWithoutIcon, {
        [style.primaryButton]: variant === "PRIMARY",
        [style.secondaryButton]: variant === "SECONDARY",
        [style.tertiaryButton]: variant === "TERTIARY",
        [style.icon]: buttonHasIcon,
        [style.loading]: loadingAnimation,
        [style.deleteButton]: icon === "DELETE",
        [style.smallButton]: isSmallButton,
    });

    const tooltipProperties = {
        "data-tip": tooltipText,
        "data-effect": "solid",
    };

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    return (
        <>
            <button
                {...buttonProps}
                ref={ref}
                className={buttonClasses}
                data-testid={testId}
                {...(tooltipText && tooltipProperties)}
                disabled={props.disabled}
            >
                {buttonIcon()}
                {buttonProps.children}
            </button>
        </>
    );
});

export default Button;
